// pages/TarificationPage.tsx
import React from "react";
import styled, { keyframes } from "styled-components";

const FaChild = require("react-icons/fa").FaChild;
const FaMoneyBill = require("react-icons/fa").FaMoneyBill;

const primaryColor = "#15b5b0";
const textColor = "#333";

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/* Container global */
export const TarificationContainer = styled.div`
  max-width: 1200px;
  margin: auto;
  padding: 0px 20px 25px 20px;
  animation: ${fadeIn} 0.8s ease-in-out;
`;

/* Titre principal */
export const Title = styled.h1`
  font-family: "Baloo", cursive;
  color: ${primaryColor};
  text-align: center;
  font-size: 3rem;
  margin-bottom: 40px;
`;

/* Container pour les tableaux (fond blanc, arrondi, ombre) */
const TablesCard = styled.div`
  background-color: #fff;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 40px;
`;

/* Container pour chaque tableau individuel */
const TableContainer = styled.div`
  margin-bottom: 20px;
`;

/* Styled table */
const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-bottom: 20px;
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
`;

/* Légende du tableau */
const TableCaption = styled.caption`
  font-size: 1.2rem;
  font-weight: bold;
  color: ${primaryColor};
  padding: 10px 0;
  caption-side: top;
  background: #fff;
`;

/* En-têtes du tableau */
const TableHeader = styled.th`
  background-color: ${primaryColor};
  color: #fff;
  padding: 12px;
  border-bottom: 1px solid #ddd;
  text-align: center;
  font-size: 1rem;
`;

/* Cellules de données */
const TableCell = styled.td`
  padding: 12px;
  border-bottom: 1px solid #ddd;
  text-align: center;
  font-size: 0.95rem;
  color: ${textColor};
  background: #fff;
`;

/* Pour la dernière ligne, pas de bordure inférieure */
const TableRow = styled.tr`
  &:last-child td {
    border-bottom: none;
  }
`;

/* Grid pour les sections textuelles supplémentaires */
const SectionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr; /* Une seule colonne en mobile */
  }
`;

/* Carte pour la section "Types d’accueil" et "Aide financière" */
const InfoCard = styled.div`
  background-color: #fff;
  border-radius: 16px;
  padding-right: 30px;
  padding-left: 30px;
  padding-bottom: 30px;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0px 6px 12px rgba(0, 0, 0, 0.1);
  }
`;

/* Titre de la carte InfoCard */
const InfoTitle = styled.h2`
  font-family: "Baloo", cursive;
  font-size: 2rem;
  color: ${primaryColor};
  margin-bottom: 15px;
  display: flex;
  align-items: center;
`;

/* Texte explicatif de la carte */
const InfoText = styled.p`
  font-family: "Patrick Hand", cursive;
  line-height: 1.8;
  margin-bottom: 15px;
  font-size: 1.125rem;
  color: ${textColor};
`;

/* Liste */
const InfoList = styled.ul`
  margin-left: 25px;
  list-style-type: disc;
  margin-bottom: 15px;
  color: ${textColor};
  font-size: 1.125rem;
`;

const InfoListItem = styled.li`
  margin-bottom: 8px;
`;

/* Bouton "En savoir plus" */
const MoreInfoButton = styled.a`
  display: inline-block;
  margin-top: 15px;
  padding: 10px 20px;
  background: ${primaryColor};
  color: #fff;
  text-decoration: none;
  font-family: "Baloo", cursive;
  font-size: 1rem;
  border-radius: 8px;
  transition: background 0.3s ease;

  &:hover {
    background: #5d8a98;
  }
`;

const TarificationPage: React.FC = () => {
  return (
    <TarificationContainer>
      <Title>Tarification</Title>

      <p>
        💡 Un tarif “tout inclus” et annualisé.
        <br />
        <br />
        Pour plus de simplicité et de transparence, notre tarif comprend tout :
        repas et goûters 100 % bio, produits d’hygiène, couches, matériel
        d’activités et interventions pédagogiques.
      </p>
      <TablesCard>
        <TableContainer>
          <StyledTable>
            <TableCaption>
              Plafonds de revenus 2023 en vigueur jusqu'au 31 décembre 2025 pour
              prise en charge CAF
            </TableCaption>
            <thead>
              <tr>
                <TableHeader>Enfant(s) à charge</TableHeader>
                <TableHeader>Tranche 1 : revenus inférieurs à</TableHeader>
                <TableHeader>Tranche 2 : revenus ne dépassant pas</TableHeader>
                <TableHeader>Tranche 3 : revenus supérieurs à</TableHeader>
              </tr>
            </thead>
            <tbody>
              <TableRow>
                <TableCell>1 enfant</TableCell>
                <TableCell>23 903€</TableCell>
                <TableCell>53 119€</TableCell>
                <TableCell>53 119€</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>2 enfants</TableCell>
                <TableCell>27 295€</TableCell>
                <TableCell>60 659€</TableCell>
                <TableCell>60 659€</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>3 enfants</TableCell>
                <TableCell>30 687€</TableCell>
                <TableCell>68 199€</TableCell>
                <TableCell>68 199€</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Par enfant ou plus</TableCell>
                <TableCell>+ 3 392€</TableCell>
                <TableCell>+ 7 540€</TableCell>
                <TableCell>+ 7 540€</TableCell>
              </TableRow>
            </tbody>
          </StyledTable>
        </TableContainer>
        <TableContainer>
          <StyledTable>
            <TableCaption>
              Montants mensuels maximum remboursement CAF du 1er avril 2025 au
              31 mars 2026
            </TableCaption>
            <thead>
              <tr>
                <TableHeader>Âge de l'enfant gardé</TableHeader>
                <TableHeader>Tranche 1</TableHeader>
                <TableHeader>Tranche 2</TableHeader>
                <TableHeader>Tranche 3</TableHeader>
              </tr>
            </thead>
            <tbody>
              <TableRow>
                <TableCell>- de 3 ans</TableCell>
                <TableCell>984,26€</TableCell>
                <TableCell>848,47€</TableCell>
                <TableCell>712,72€</TableCell>
              </TableRow>
            </tbody>
          </StyledTable>
        </TableContainer>
        <p>Un montant minimum de 15% de la dépense restera à votre charge.</p>
      </TablesCard>

      <SectionsGrid>
        <InfoCard>
          <InfoTitle>
            <FaMoneyBill /> Aide financière
          </InfoTitle>
          <InfoText>
            Vous pouvez bénéficier d’un crédit d’impôt sur le revenu grâce à la
            garde de votre enfant. Le montant est égal à 50% des sommes versées
            avec un plafond maximal de 3 500€/an soit jusqu’à 1 750€ remboursés
            par année.
          </InfoText>
          <MoreInfoButton
            href="https://www.service-public.fr/particuliers/vosdroits/F8"
            target="_blank"
            rel="noopener noreferrer"
          >
            En savoir plus
          </MoreInfoButton>
        </InfoCard>

        <InfoCard>
          <InfoTitle>
            <FaChild /> Types d’accueil
          </InfoTitle>
          <InfoText>
            Nous proposons plusieurs types d’accueil adaptés aux besoins de
            chaque famille.
          </InfoText>
          <InfoList>
            <InfoListItem>
              <strong>L’accueil régulier :</strong> inscription sur contrat
              individualisé avec des jours et heures définis. Les enfants en
              contrat sont prioritaires. (Contrats jusqu’au 31 août.)
            </InfoListItem>
            <InfoListItem>
              <strong>L’accueil occasionnel ou d’urgence :</strong> inscription
              minimum 48h à l’avance ou en dernière minute en fonction des
              places disponibles. Accueil non planifié.
            </InfoListItem>
          </InfoList>
        </InfoCard>
      </SectionsGrid>
    </TarificationContainer>
  );
};

export default TarificationPage;
